import React, { useEffect, useMemo, useState } from 'react';

import {
  AppstoreOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import SelectNotFoundContent from '@components/shared/ui/SelectNotFoundContent';
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  Select,
  Space,
  message,
} from 'antd';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  AUTH_CREDENTIAL_KEYS,
  OAUTH_PLATFORMS,
  PLATFORMS_WITH_CUSTOM_APP_SUPPORT,
  buildEcAccountPlatformOptions,
} from '@/constants/ecAccountPlatforms';
import { getDeploymentFlags } from '@/utils/deploymentFlags';

import BvShopCredentials from './BvShopCredentials';
import CoupangCredentials from './CoupangCredentials';
import CyberbizCredentials from './CyberbizCredentials';
import EasyStoreCredentials from './EasyStoreCredentials';
import { ConnectionStatusTag } from './EcAccountTags';
import LiteshopCredentials from './LiteshopCredentials';
import MomoCredentials from './MomoCredentials';
import MoPlusCredentials from './MoPlusCredentials';
import NineOneAppCredentials from './NineOneAppCredentials';
import PchomeCredentials from './PchomeCredentials';
import ShopeeCredentials from './ShopeeCredentials';
import ShopifyCredentials from './ShopifyCredentials';
import ShoplineCredentials from './ShoplineCredentials';
import WoocommerceCredentials from './WoocommerceCredentials';
import YahooScmCredentials from './YahooScmCredentials';

// Credentials 組件映射
const CREDENTIALS_COMPONENTS: Record<string, React.ComponentType<any>> = {
  Shopify: ShopifyCredentials,
  Shopee: ShopeeCredentials,
  Shopline: ShoplineCredentials,
  Liteshop: LiteshopCredentials,
  NineOneApp: NineOneAppCredentials,
  Momo: MomoCredentials,
  Woocommerce: WoocommerceCredentials,
  Cyberbiz: CyberbizCredentials,
  YahooScm: YahooScmCredentials,
  Pchome: PchomeCredentials,
  MoPlus: MoPlusCredentials,
  EasyStore: EasyStoreCredentials,
  BvShop: BvShopCredentials,
  Coupang: CoupangCredentials,
};

interface SalesChannelOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface EcAccountRecord {
  id?: string;
  merchant_id?: string | null;
  credentials?: Record<string, any> | null;
  [key: string]: unknown;
}

interface ConnectionPanelProps {
  // antd Form is generic; the form values shape varies by mode.
  form: FormInstance<any>;
  mode: 'create' | 'edit';
  role: 'admin' | 'operator';
  ecAccount: EcAccountRecord | null;
  // ecAccountService 是 .js service，方法多樣（create / update / testConnection /
  // getCoupangShippingInfo / fetchList ...），先 fall back 為 any。
  ecAccountService: any;
  // Merchant-scoped reference data injected by wrapper. Shared panel stays
  // merchant-agnostic — never self-fetches sales_channels by merchant_id, which
  // would race with the operator's current header-merchant vs the loaded record's
  // merchant and let A's record save B's sales_channel_id.
  salesChannels: SalesChannelOption[];
  // True while the wrapper is still switching OperatorDataContext.currentMerchant
  // to the record's merchant. Used to show channelsLoading on the Select.
  salesChannelsLoading?: boolean;
  connectionStatus: string;
  onSave: (opts?: { skipValidation?: boolean }) => Promise<EcAccountRecord | null>;
  onConnectionStatusChange: (status: string) => void;
}

const ConnectionPanel: React.FC<ConnectionPanelProps> = ({
  form,
  mode,
  role,
  ecAccount,
  ecAccountService,
  salesChannels,
  salesChannelsLoading = false,
  connectionStatus,
  onSave,
  onConnectionStatusChange,
}) => {
  const { t } = useTranslation();

  const [testLoading, setTestLoading] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [verifiedCredentials, setVerifiedCredentials] = useState<Record<string, any>>(
    () => ecAccount?.credentials || {},
  );
  // 貨主自備 Custom App：開啟後展開 client id / secret 欄位，授權改用貨主憑證。
  // 編輯既有帳號時，若 credentials 已帶 client_id 代表先前就是用 custom app。
  const [useCustomApp, setUseCustomApp] = useState<boolean>(
    () => !!ecAccount?.credentials?.client_id,
  );

  const moduleName = Form.useWatch('module_name', form) as string | undefined;

  const isOperator = role === 'operator';
  const isOAuth = !!moduleName && OAUTH_PLATFORMS.includes(moduleName);
  // deployment 開啟 support_custom_apps 且平台支援時，才提供 custom app 入口
  const canUseCustomApp = useMemo(
    () =>
      !!getDeploymentFlags().support_custom_apps &&
      !!moduleName &&
      PLATFORMS_WITH_CUSTOM_APP_SUPPORT.includes(moduleName),
    [moduleName],
  );

  useEffect(() => {
    if (ecAccount?.id) {
      setVerifiedCredentials(ecAccount.credentials || {});
      setUseCustomApp(!!ecAccount.credentials?.client_id);
    } else {
      setVerifiedCredentials({});
      setUseCustomApp(false);
    }
  }, [ecAccount?.id]);

  // API Key 平台：測試連線
  const handleTestConnection = async () => {
    try {
      await form.validateFields([
        'module_name',
        'display_name',
        'sales_channel_id',
      ]);
    } catch {
      return;
    }

    setTestLoading(true);
    setConnectionError(null);
    try {
      const saved = await onSave({ skipValidation: true });
      if (!saved) {
        setTestLoading(false);
        return;
      }

      const response = await ecAccountService.testConnection(saved.id);
      if (response.success) {
        message.success(t('models.ec_account.test_connection_success'));
        // 連線成功但平台自動設定有缺（例：WooCommerce API 金鑰無寫入權限 → webhook
        // 建不了，退回 polling）。顯式列出 code，不用動態 t() key（見 I18N.md §4.1）。
        if (response.warning === 'webhook_write_permission_missing') {
          message.warning(
            t('models.ec_account.test_connection_warning.webhook_write_permission_missing'),
          );
        } else if (response.warning) {
          message.warning(
            t('models.ec_account.test_connection_warning.webhook_registration_failed'),
          );
        }
        onConnectionStatusChange('connected');
        setVerifiedCredentials(form.getFieldValue('credentials') || {});
      } else {
        // FRONTEND.md §2：後端回的 message 是 error code（如 "integration_request_failed"），
        // 不是給人看的字串。先試 i18n 翻譯該 code、翻不到 fallback 用 errors[0].detail
        // （平台 API 原始 message，例如 401 Unauthorized）、再不行才用通用「連線失敗」。
        // 嚴禁直接把 code 字串塞到 UI（使用者會看到 "integration_request_failed"）。
        const code = response.message;
        const i18nKey = code ? `errors.${code}` : null;
        const translated = i18nKey ? t(i18nKey, { defaultValue: '' }) : '';
        const vendorDetail = response.errors?.[0]?.detail;
        setConnectionError(
          translated ||
            vendorDetail ||
            t('models.ec_account.test_connection_failed'),
        );
        onConnectionStatusChange('disconnected');
        setVerifiedCredentials(form.getFieldValue('credentials') || {});
      }
    } catch (error) {
      // 走到 catch 只有 network / JSON parse / 程式 bug 三種，都不是「使用者填錯憑證」
      // 那種可動作的失敗。一律用 unexpected_error 通用文案 + console.error 完整 stack，
      // 不要寫啟發式 (err.name === 'TypeError') 拼成「網路錯誤」誤導 ops。
      console.error('[ConnectionPanel] testConnection failed:', error);
      setConnectionError(t('errors.unexpected_error'));
      onConnectionStatusChange('disconnected');
      setVerifiedCredentials(form.getFieldValue('credentials') || {});
    } finally {
      setTestLoading(false);
    }
  };

  // OAuth 平台：授權（自動儲存 + 跳轉到第三方授權頁面）
  const handleAuthorize = async () => {
    try {
      const fieldsToValidate: Array<string | string[]> = [
        'module_name',
        'display_name',
        'sales_channel_id',
      ];
      // Shopify 至少需要填寫商店網域（api_key / code 可由 ENV 提供）
      if (moduleName === 'Shopify') {
        fieldsToValidate.push(['credentials', 'api_domain']);
      }
      // 貨主自備 Custom App 時，client id / secret 為必填（授權改用此組憑證）
      if (canUseCustomApp && useCustomApp) {
        fieldsToValidate.push(
          ['credentials', 'client_id'],
          ['credentials', 'client_secret'],
        );
      }
      await form.validateFields(fieldsToValidate as any);
    } catch {
      return;
    }

    setAuthorizing(true);
    try {
      const saved = await onSave({ skipValidation: true });
      if (!saved?.id) {
        setAuthorizing(false);
        return;
      }

      // 統一走 /third_parties/{platform}/auth
      const pathname = window.location.pathname;
      const authMode = pathname.endsWith('/new') ? 'new' : 'edit';
      const source = pathname.startsWith('/admin') ? 'admin' : 'operator';
      const platform = moduleName!.toLowerCase();
      window.location.href = `/third_parties/${platform}/auth?ec_account_id=${saved.id}&source=${source}&mode=${authMode}`;
    } catch (error) {
      console.error('Authorization failed:', error);
      setAuthorizing(false);
    }
  };

  // OAuth 平台：取消授權（跳轉到第三方取消授權頁面）
  const handleRevoke = () => {
    if (!ecAccount?.id) return;

    const pathname = window.location.pathname;
    const revokeMode = pathname.endsWith('/new') ? 'new' : 'edit';
    const source = pathname.startsWith('/admin') ? 'admin' : 'operator';
    const platform = (moduleName ?? '').toLowerCase();
    window.location.href = `/third_parties/${platform}/cancel_auth?ec_account_id=${ecAccount.id}&source=${source}&mode=${revokeMode}`;
  };

  // 取得 credentials 中 access_token 狀態（用於判斷 OAuth 授權是否過期）
  const watchCredentials = (Form.useWatch('credentials', form) as Record<string, any> | undefined) || {};
  const hasAccessTokenKey = 'access_token' in watchCredentials;
  const hasAccessTokenValue = !!watchCredentials?.access_token;
  // 僅「曾經授權過但 token 已失效」才算過期，從未連線過的不算
  const isAuthExpired =
    hasAccessTokenKey &&
    !hasAccessTokenValue &&
    connectionStatus !== 'never_tested';

  // 編輯模式下，若使用者明確輸入新 credentials 值（忽略遮罩值），切換 UI 狀態為未驗證以便重新測試連線
  // 僅比對真正影響連線驗證的欄位（AUTH_CREDENTIAL_KEYS），後設定欄位（如 Coupang 退貨地代碼）改動不重置狀態
  const credentialsChanged = useMemo(() => {
    if (mode !== 'edit') return false;
    const baseline = verifiedCredentials || {};
    const incoming = watchCredentials || {};
    const authKeys = moduleName
      ? (AUTH_CREDENTIAL_KEYS as Record<string, string[] | undefined>)[moduleName]
      : undefined;
    const keysToCheck = authKeys || Object.keys(incoming);
    return keysToCheck.some((k: string) => {
      const v = incoming[k];
      if (v === '********' || v === undefined) return false;
      return baseline[k] !== v;
    });
  }, [mode, moduleName, verifiedCredentials, watchCredentials]);
  const effectiveStatus = credentialsChanged
    ? 'never_tested'
    : connectionStatus;

  const CredentialsComponent = moduleName
    ? CREDENTIALS_COMPONENTS[moduleName]
    : null;

  return (
    <div>
      {/* 標題 + 連線狀態 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 600 }}>
          {t('models.ec_account.connection_info', { defaultValue: '連線資訊' })}
        </span>
        <ConnectionStatusTag status={effectiveStatus} />
      </div>

      <Divider style={{ marginTop: 0 }} />

      {/* 平台選擇 */}
      <Form.Item
        name="module_name"
        label={t('models.ec_account.module_name')}
        rules={[{ required: true, message: t('common.validation.required') }]}
      >
        <Select
          disabled={mode === 'edit'}
          showSearch
          optionFilterProp="label"
          placeholder={t('common.placeholder.please_select')}
          options={buildEcAccountPlatformOptions(t)}
        />
      </Form.Item>

      {/* 顯示名稱 */}
      <Form.Item
        name="display_name"
        label={t('models.ec_account.display_name')}
        rules={[{ required: true, message: t('common.validation.required') }]}
      >
        <Input placeholder={t('common.placeholder.please_enter')} />
      </Form.Item>

      {/* 銷售通路 */}
      <Form.Item
        name="sales_channel_id"
        label={t('models.sales_channel.name')}
        rules={[{ required: true, message: t('common.validation.required') }]}
      >
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t('common.placeholder.please_select')}
          loading={salesChannelsLoading}
          notFoundContent={
            <SelectNotFoundContent
              hint={t('models.ec_account.no_sales_channels', {
                defaultValue: '尚未建立銷售通路',
              })}
              settingsPath={
                isOperator
                  ? '/operator/data/sales_channels'
                  : '/admin/sales_channels'
              }
              settingsLabel={t(
                'models.ec_account.go_to_sales_channel_settings',
                { defaultValue: '前往銷售通路設定' },
              )}
            />
          }
          options={salesChannels.map((channel) => ({
            value: channel.id,
            label: channel.name || channel.id,
          }))}
        />
      </Form.Item>

      {/* 憑證設定 */}
      {CredentialsComponent && (
        <>
          <Divider orientation="left">
            {t('models.ec_account.credentials_settings')}
          </Divider>
          <CredentialsComponent
            form={form}
            ecAccount={ecAccount}
            ecAccountService={ecAccountService}
            onSave={onSave}
            useCustomApp={canUseCustomApp && useCustomApp}
          />
        </>
      )}

      {/* 操作按鈕區域 */}
      {CredentialsComponent && (
        <div style={{ marginTop: 16 }}>
          {isOAuth ? (
            <>
              {isAuthExpired && (
                <Alert
                  message={t('models.ec_account.oauth_expired', {
                    defaultValue: '授權已過期',
                  })}
                  description={t(
                    'models.ec_account.oauth_expired_description',
                    { defaultValue: '請重新授權以恢復連線' },
                  )}
                  type="error"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              )}
              {moduleName === 'Shopee' && connectionStatus !== 'connected' && (
                <Alert
                  message={t(
                    'models.ec_account.platform_settings.shopee.shop_account_notice',
                  )}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              )}
              {connectionStatus === 'connected' ? (
                <Button danger icon={<UnlockOutlined />} onClick={handleRevoke}>
                  {t('models.ec_account.revoke_authorization', {
                    defaultValue: '取消授權',
                  })}
                </Button>
              ) : (
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<LockOutlined />}
                    onClick={handleAuthorize}
                    loading={authorizing}
                  >
                    {t('models.ec_account.authorize', {
                      defaultValue: '授權連線',
                    })}
                  </Button>
                  {canUseCustomApp && (
                    <Button
                      icon={<AppstoreOutlined />}
                      type={useCustomApp ? 'primary' : 'default'}
                      ghost={useCustomApp}
                      onClick={() => setUseCustomApp((prev) => !prev)}
                    >
                      {t('models.ec_account.use_custom_app', {
                        defaultValue: '使用自訂 App',
                      })}
                    </Button>
                  )}
                </Space>
              )}
            </>
          ) : (
            !isAuthExpired && (
              <>
                {effectiveStatus === 'connected' ? (
                  <Button disabled>
                    ✓{' '}
                    {t('models.ec_account.test_connection_success_button', {
                      defaultValue: '連線測試成功',
                    })}
                  </Button>
                ) : (
                  <Button onClick={handleTestConnection} loading={testLoading}>
                    {t('models.ec_account.test_connection')}
                  </Button>
                )}

                {/* 連線錯誤提示 */}
                {connectionError && (
                  <div
                    style={{
                      marginTop: 12,
                      borderRadius: 8,
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(255, 77, 79, 0.15)',
                    }}
                  >
                    <Alert
                      message={
                        <strong>
                          {t('models.ec_account.test_connection_failed')}
                        </strong>
                      }
                      description={
                        <div
                          style={{
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            marginTop: 4,
                          }}
                        >
                          {connectionError}
                        </div>
                      }
                      type="error"
                      showIcon
                      style={{
                        textAlign: 'left',
                        border: '1px solid #ffccc7',
                        backgroundColor: '#fff2f0',
                      }}
                    />
                  </div>
                )}
              </>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectionPanel;
